// Редовете на червената лента най-горе на таблото — САМО критичните сигнали
// (`criticalBoxSlugs`), всеки с текст и действие: незавършена инвентаризация
// (по ред на броене, моята първа), закъснели отчети, задържани доставки,
// неплатени над 14 дни. Лентата показва до 3 реда; останалото е в кутиите.
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { AttentionBannerItem } from "../../components/list/AttentionBanner";
import { isMineDraft, unfinishedStocktakeLine } from "../../stocktake/components/home/stocktakeUnfinished";
import {
  criticalBoxSlugs,
  type DashboardSummaries,
  type DeliveryDraftsSummary,
  type PaymentsSummary,
  type PostingSummary,
  type StocktakeSummary,
} from "../dashboardModel";
import type { DashboardDrafts } from "./useDashboardBoxes";

export const useDashboardAlerts = (
  summaries: DashboardSummaries,
  drafts: DashboardDrafts,
): AttentionBannerItem[] => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items: AttentionBannerItem[] = [];
  for (const slug of criticalBoxSlugs(summaries)) {
    const box = summaries[slug];
    if (box.state !== "ready") continue;
    switch (slug) {
      case "stocktake": {
        const mineDraft = drafts.stocktake.mine;
        for (const draft of (box.value as StocktakeSummary).unfinished) {
          const mine = isMineDraft(draft, mineDraft);
          items.push({
            key: `stocktake-${draft.key}`,
            text: t("inventoryPage.dashAlertStocktake", {
              line: unfinishedStocktakeLine(t, draft, { mine, withWarehouse: true }),
            }),
            actionLabel: mine ? t("inventoryPage.dashContinue") : t("inventoryPage.dashTakeOver"),
            onAction: draft.onOpen,
          });
        }
        break;
      }
      case "posting": {
        const summary = box.value as PostingSummary;
        const oldest = summary.oldest;
        items.push({
          key: "posting",
          text: oldest
            ? t("inventoryPage.dashAlertOverdue", {
                count: summary.overdue,
                number: oldest.reportNumber,
                date: dayjs(oldest.reportDate).format("DD.MM"),
              })
            : t("inventoryPage.dashOverdue", { count: summary.overdue }),
          actionLabel: t("inventoryPage.dashPostOldest"),
          onAction: () =>
            navigate("/inventory/posting", oldest ? { state: { selectReportId: oldest.id } } : undefined),
        });
        break;
      }
      case "delivery":
        items.push({
          key: "delivery",
          text: t("inventoryPage.dashAlertHeld", { count: (box.value as DeliveryDraftsSummary).held }),
          actionLabel: t("inventoryPage.dashAlertOpen"),
          onAction: () => navigate("/inventory/delivery"),
        });
        break;
      case "delivery-payments":
        items.push({
          key: "payments",
          text: t("inventoryPage.dashAlertOverduePay", { count: (box.value as PaymentsSummary).overdue }),
          actionLabel: t("inventoryPage.dashPay"),
          onAction: () => navigate("/inventory/delivery-payments"),
        });
        break;
    }
  }
  return items;
};
